import { AttachmentInfoBarMessageId } from './AttachmentInfoBarMessageId';
import AttachmentUploadQueueItem from '../../types/AttachmentUploadQueueItem';
import InfoBarMessageViewStateCreator from 'owa-info-bar/lib/schema/InfoBarMessageViewStateCreator';
import loc, { format } from 'owa-localize';
import {
    WarningFilesCanNotBeAttachedWithNamesGenericError,
    WarningSeveralFilesCanNotBeAttachedGenericError,
    WarningSingleFileCannotBeAttachedWithFallback,
    WarningFilesCannotBeAttachedFromThatMailbox,
    TryAgain,
} from './showErrorMessagesForUploadFailures.locstring.json';
import {
    InfoBarCustomAction,
    InfoBarMessageRank,
    InfoBarMessageSource,
    InfoBarMessageViewState,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';

const MAX_NUMBER_OF_FAILED_ATTACHMENT_NAMES_IN_INFO_BAR = 4;

// TODO VSO 51804 - add cannot attach to self
export const enum AttachmentUploadFailureReason {
    GenericError,
    NonExistentMailbox,
}

export interface AttachmentUploadItemFailure {
    item: AttachmentUploadQueueItem;
    reason: AttachmentUploadFailureReason;
}

function groupBy<T>(
    list: T[],
    groupByProperty: (val: T) => string | number
): { [key: number]: T[] } {
    return list.reduce(
        (groups, val) => {
            const prop = groupByProperty(val);
            if (!groups[prop]) {
                groups[prop] = [val];
            } else {
                groups[prop].push(val);
            }

            return groups;
        },
        {} as { [key: number]: T[] }
    );
}

function addMessageToInfoBar(
    infoMessageId: AttachmentInfoBarMessageId,
    message: string,
    infoBarAddHandler: (
        messageId: string,
        infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
    ) => void
) {
    infoBarAddHandler(infoMessageId, [message]);
}

function showGenericErrorMessage(
    failures: AttachmentUploadItemFailure[],
    infoBarAddHandler: (
        messageId: string,
        infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
    ) => void
) {
    let message = null;
    let messageId = null;
    if (failures.length === 1 && failures[0].item.tryAgainCallback !== null) {
        const failure = failures[0];
        const fileName = failure.item.file.name;

        const infoBarMessageViewState: InfoBarMessageViewState = {
            key: 'WarningFilesCanNotBeAttachedWithNamesGenericError',
            source: InfoBarMessageSource.Compose,
            rank: InfoBarMessageRank.Info,
            messageParts: [format(loc(WarningSingleFileCannotBeAttachedWithFallback), fileName)],
        };
        const ignoreCustomAction: InfoBarCustomAction = {
            text: loc(TryAgain),
            action: () => failure.item.tryAgainCallback(),
        };
        infoBarMessageViewState.messageParts.push(ignoreCustomAction);
        infoBarAddHandler(
            'WarningFilesCanNotBeAttachedWithNamesGenericError',
            (viewState: any) => infoBarMessageViewState
        );
        return;
    } else if (failures.length < MAX_NUMBER_OF_FAILED_ATTACHMENT_NAMES_IN_INFO_BAR) {
        const fileNames = failures.map(failure => failure.item.file.name).join(', ');
        messageId = 'WarningFilesCanNotBeAttachedWithNamesGenericError';
        message = format(loc(WarningFilesCanNotBeAttachedWithNamesGenericError), fileNames);
    } else {
        messageId = 'WarningSeveralFilesCanNotBeAttachedGenericError';
        message = format(loc(WarningSeveralFilesCanNotBeAttachedGenericError), failures.length);
    }

    addMessageToInfoBar(messageId, message, infoBarAddHandler);
}

function showNonExistentMailboxErrorMessage(
    failures: AttachmentUploadItemFailure[],
    infoBarAddHandler: (
        messageId: string,
        infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
    ) => void
) {
    const message = loc(WarningFilesCannotBeAttachedFromThatMailbox);
    const messageId = 'WarningFilesCannotBeAttachedFromOtherMailboxes';
    addMessageToInfoBar(messageId, message, infoBarAddHandler);
}

export default function showErrorMessagesForUploadFailures(
    failures: AttachmentUploadItemFailure[],
    infoBarAddHandler: (
        messageId: string,
        infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
    ) => void
): void {
    // Creating a dictionary which has failures grouped by reason
    const groupedFailuresByReason = groupBy<AttachmentUploadItemFailure>(
        failures,
        failure => failure.reason
    );

    // Going each of the failure reason and showing message
    const failureReasons = Object.keys(groupedFailuresByReason);
    failureReasons.forEach(reason => {
        const failureGroup: AttachmentUploadItemFailure[] = groupedFailuresByReason[reason];
        switch (parseInt(reason)) {
            case AttachmentUploadFailureReason.GenericError:
                // Even though we have the infoBarHandler as part of the failure group
                // as usually it is the same so we are just passing in the first one
                showGenericErrorMessage(failureGroup, infoBarAddHandler);
                break;
            case AttachmentUploadFailureReason.NonExistentMailbox:
                showNonExistentMailboxErrorMessage(failureGroup, infoBarAddHandler);
                break;
        }
    });
}
